/* AQUON Flavour Hero - dependency-free looping timeline.
   Stages per cycle: headline -> pack -> side (pills + details) -> hold -> exit -> next flavour.
   Transform/opacity only (driven by CSS state classes). Pauses off-screen and when tab hidden.
   Flavour pills are clickable to jump. Respects prefers-reduced-motion. */
(function () {
  'use strict';

  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function initHero(root) {
    if (root.__afhInit) return;
    root.__afhInit = true;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var hold = parseInt(root.dataset.afhHold, 10) || 2000;
    var reveal = parseInt(root.dataset.afhReveal, 10) || 600;
    var stagger = parseInt(root.dataset.afhStagger, 10) || 150;
    var autoplay = root.dataset.afhAutoplay !== 'false';

    var bgs = qsa('[data-afh-bg]', root);
    var packs = qsa('[data-afh-pack]', root);
    var pills = qsa('[data-afh-pill]', root);
    var details = qsa('[data-afh-detail]', root);
    var claims = qsa('[data-afh-claim]', root);
    var count = Math.max(packs.length, details.length, bgs.length, pills.length, 1);

    var index = 0;
    var inView = false;
    var token = 0; // increments to cancel an in-flight cycle

    var sachets = qsa('[data-afh-sachet]', root);

    function activate(i) {
      [bgs, packs, pills, details, sachets].forEach(function (list) {
        list.forEach(function (el, k) { el.classList.toggle('is-active', k === i); });
      });
      pills.forEach(function (p, k) { p.setAttribute('aria-selected', k === i ? 'true' : 'false'); });
    }

    // cancellable wait; resolves early if the cycle token changed
    function wait(ms, myToken) {
      return new Promise(function (resolve) {
        var t = setTimeout(resolve, ms);
        var iv = setInterval(function () {
          if (myToken !== token) { clearTimeout(t); clearInterval(iv); resolve('cancel'); }
        }, 60);
        setTimeout(function () { clearInterval(iv); }, ms + 80);
      });
    }

    // wait until the hero is on screen and the tab is visible
    function waitForView(myToken) {
      return new Promise(function (resolve) {
        (function check() {
          if (myToken !== token) return resolve('cancel');
          if (inView && document.visibilityState !== 'hidden') return resolve();
          setTimeout(check, 150);
        })();
      });
    }

    function clearStages() {
      root.classList.remove('is-headline', 'is-pack', 'is-side', 'is-exit');
    }

    async function cycle() {
      var myToken = ++token;
      clearStages();
      // reduced motion: just show everything, no looping
      if (reduce) {
        activate(index);
        root.classList.add('is-headline', 'is-pack', 'is-side');
        return;
      }

      while (myToken === token) {
        if (autoplay) {
          var v = await waitForView(myToken);
          if (v === 'cancel') return;
        }

        activate(index);

        // Stage 1: headline
        root.classList.add('is-headline');
        if ((await wait(reveal + stagger * Math.max(claims.length - 1, 0), myToken)) === 'cancel') return;

        // Stage 2: product
        root.classList.add('is-pack');
        if ((await wait(reveal, myToken)) === 'cancel') return;

        // Stage 3: side content (pills stagger + details)
        root.classList.add('is-side');
        if ((await wait(reveal + stagger * Math.max(count - 1, 0), myToken)) === 'cancel') return;

        if (!autoplay) return; // single reveal, no loop

        // Stage 4: hold
        if ((await wait(hold, myToken)) === 'cancel') return;

        // Stage 5: exit (reverse)
        root.classList.add('is-exit');
        root.classList.remove('is-headline', 'is-pack', 'is-side');
        if ((await wait(reveal + 260, myToken)) === 'cancel') return;

        clearStages();
        index = (index + 1) % count;
      }
    }

    function jumpTo(i) {
      index = i;
      cycle(); // bumps token, restarts from this flavour
    }

    pills.forEach(function (pill, k) {
      pill.addEventListener('click', function () { jumpTo(k); });
    });

    // Visibility control
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { inView = e.isIntersecting; });
      }, { threshold: 0.25 });
      io.observe(root);
    } else {
      inView = true;
    }
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible' && inView) { /* loop resumes via waitForView */ }
    });

    cycle();

    // Re-init in the theme editor when the section reloads
    document.addEventListener('shopify:section:load', function (e) {
      if (root.closest && e.target && e.target.contains(root)) { token++; }
    });
  }

  /* ---------- Scroll story (pinned, dependency-free) ---------- */
  function setActiveFlavour(hero, i) {
    ['[data-afh-bg]', '[data-afh-pack]', '[data-afh-pill]', '[data-afh-detail]', '[data-afh-sachet]'].forEach(function (sel) {
      qsa(sel, hero).forEach(function (el, k) { el.classList.toggle('is-active', k === i); });
    });
    qsa('[data-afh-pill]', hero).forEach(function (p, k) { p.setAttribute('aria-selected', k === i ? 'true' : 'false'); });
  }

  function initScroll(track) {
    if (track.__afhScroll) return;
    track.__afhScroll = true;
    var hero = track.querySelector('[data-afh-mode="scroll"]');
    if (!hero) return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    setActiveFlavour(hero, 0);

    // Flavour pills switch the active flavour (bg/pack/details/name). The GSAP
    // timeline animates the CONTAINERS, so whichever flavour is active reveals -
    // no rebuild needed.
    qsa('[data-afh-pill]', hero).forEach(function (pill, k) {
      pill.addEventListener('click', function () { setActiveFlavour(hero, k); });
    });

    // Reduced motion, or a genuinely low-powered device: skip the scroll story
    // and just show everything (no pin, no animation).
    var lowPower = (navigator.deviceMemory && navigator.deviceMemory <= 2) ||
      (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2);
    if (reduce || lowPower) {
      hero.classList.add('is-static');
      return;
    }

    // Prefer GSAP (rich scrubbed timeline); fall back to the dependency-free class
    // engine. We deliberately do NOT use ScrollTrigger's pin: the theme runs Lenis
    // smooth-scroll, and ScrollTrigger's pin/refresh math miscalibrates against it
    // (start goes negative -> the whole story sits above the reachable scroll range
    // and the section renders blank). Instead we pin with CSS sticky and drive the
    // timeline from the track's live getBoundingClientRect(), which is always
    // visually correct regardless of smooth-scroll.
    if (window.gsap) {
      buildGsapTimeline(track, hero);
    } else {
      initScrollClasses(track, hero);
    }
  }

  /* ---------- GSAP master timeline, rect-driven (preferred) ----------
     Pinned, scroll-scrubbed storytelling: each headline reveals one line at a
     time with a masked upward reveal; the product pops in at the final step with
     the sachet travelling from the pack centre up to its headline position, plus
     the pills and details - one synchronized composition. transform + opacity
     only, smooth power3/back easing, no bounce/elastic.

     The section is pinned with CSS sticky (the tall .is-active track) and the
     PAUSED timeline is scrubbed by a rAF loop that reads the track's live
     getBoundingClientRect(). This is immune to the theme's Lenis smooth-scroll,
     which broke the old ScrollTrigger pin (its refresh put the trigger start at a
     negative scroll position, so the story never played and the section was blank). */
  function buildGsapTimeline(track, hero) {
    var gsap = window.gsap;

    // CSS sticky pin via the tall track. hero keeps the rest-visible base state;
    // the paused timeline supplies the inline from-states (which win over CSS).
    track.classList.add('is-active');
    hero.classList.add('afh--gsap');

    var claimInners = qsa('[data-afh-claim-inner]', hero);
    var stage = hero.querySelector('.afh__stage');       // holds every pack (active shown by CSS)
    var sachets = hero.querySelector('.afh__sachets');    // holds every sachet slot
    var pillEls = qsa('.afh__pill', hero);
    var details = hero.querySelector('.afh__details');    // holds every detail (active shown by CSS)

    // Sachet travel: pack centre -> the active sachet's rest centre. Measured while
    // elements are at CSS rest, BEFORE the timeline applies its from-states.
    var sachetDx = 0, sachetDy = 0;
    (function () {
      var sachet = hero.querySelector('.afh__sachet-slot.is-active .afh__sachet');
      var pack = hero.querySelector('.afh__pack.is-active .afh__pack-img, .afh__pack.is-active .afh__pack-ph');
      if (sachet && pack) {
        var s = sachet.getBoundingClientRect(), p = pack.getBoundingClientRect();
        sachetDx = (p.left + p.width / 2) - (s.left + s.width / 2);
        sachetDy = (p.top + p.height / 2) - (s.top + s.height / 2);
      }
    })();

    var tl = gsap.timeline({ paused: true, defaults: { ease: 'power3.out' } });

    // 1..4: reveal each claim one line at a time (masked, upward), sequential.
    claimInners.forEach(function (inner, i) {
      tl.fromTo(inner,
        { yPercent: 120, opacity: 0 },
        { yPercent: 0, opacity: 1, duration: 1 },
        i === 0 ? 0.05 : '>-0.08');
    });

    // Final step: product + sachet + side content, one composition. Animating the
    // CONTAINERS (not per-flavour elements) means it works for every flavour with
    // no rebuild, and leaves the sachet's own CSS centring/rotation untouched.
    tl.addLabel('final', '>+0.15');

    if (stage) {
      tl.fromTo(stage,
        { yPercent: 26, opacity: 0, scale: 0.9 },
        { yPercent: 0, opacity: 1, scale: 1, duration: 1.2, ease: 'back.out(1.15)' },
        'final');
    }
    if (sachets) {
      // The sachet layer travels up from the pack centre (its image keeps its CSS
      // centre/rotation - GSAP only moves the container, so nothing gets wiped).
      tl.fromTo(sachets,
        { x: sachetDx, y: sachetDy, opacity: 0 },
        { x: 0, y: 0, opacity: 1, duration: 1.3 },
        'final+=0.12');
    }
    if (pillEls.length) {
      // Fixed px offset, NOT xPercent: pills are now content-width (each hugs its
      // text), so a width-relative offset shifted each by a different amount and
      // read as ragged mid-scrub. A uniform px keeps them aligned as a group and
      // settles to 0 = centred.
      tl.fromTo(pillEls,
        { x: -40, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.7, stagger: 0.08 },
        'final+=0.25');
    }
    if (details) {
      tl.fromTo(details,
        { xPercent: 22, opacity: 0 },
        { xPercent: 0, opacity: 1, duration: 0.8 },
        'final+=0.25');
    }

    tl.progress(0); // apply the from-states immediately (section starts hidden)

    // Scroll progress through the pinned range, from the track's live position.
    // We start advancing BEFORE the section pins (while it is still entering), so
    // line 1 begins revealing almost as soon as the section scrolls into view -
    // no empty "dead zone". `lead` is how early (in px) ahead of the pin the story
    // begins: progress hits 0 when the section top is `lead` px down the viewport,
    // and reaches 1 exactly when the pin releases.
    function scrollProgress() {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var scrollable = track.offsetHeight - vh; // CSS: track is taller than the pin
      if (scrollable <= 0) return 0;
      var lead = vh * 0.5;
      var p = (lead - track.getBoundingClientRect().top) / (lead + scrollable);
      return p < 0 ? 0 : (p > 1 ? 1 : p);
    }

    // rAF loop. The theme's Lenis already smooths the scroll, so we only apply a
    // very light follow (snappy, minimal lag) so line 1 tracks the scroll almost
    // instantly instead of trailing behind. Runs only while the section is near
    // the viewport, so it costs nothing off-screen.
    var raf = 0, running = false, smoothed = -1;
    function frame() {
      var p = scrollProgress();
      smoothed = smoothed < 0 ? p : smoothed + (p - smoothed) * 0.5;
      if (Math.abs(p - smoothed) < 0.0005) smoothed = p;
      tl.progress(smoothed);
      raf = requestAnimationFrame(frame);
    }
    function start() { if (!running) { running = true; frame(); } }
    function stop() { if (running) { running = false; cancelAnimationFrame(raf); } }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { e.isIntersecting ? start() : stop(); });
      }, { rootMargin: '300px 0px 300px 0px' });
      io.observe(track);
    } else {
      start();
    }
  }

  /* ---------- Fallback: dependency-free class engine (no GSAP) ---------- */
  function initScrollClasses(track, hero) {
    var steps = parseInt(track.dataset.afhSteps, 10) || 5;
    track.classList.add('is-active'); // enables the tall track + pin

    var ticking = false;
    var lastStep = -1;
    var thresholds = [0.02, 0.24, 0.40, 0.56, 0.72];

    function update() {
      ticking = false;
      var rect = track.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var scrollable = track.offsetHeight - vh;
      var lead = Math.min(vh * 0.95, scrollable * 0.8);
      var denom = scrollable + lead;
      var progress = denom > 0 ? (lead - rect.top) / denom : 0;
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;

      var step = 0;
      for (var k = 0; k < thresholds.length; k++) {
        if (progress >= thresholds[k]) step = k + 1;
      }
      if (step !== lastStep) {
        for (var s = 1; s <= steps; s++) hero.classList.toggle('is-s' + s, step >= s);
        lastStep = step;
      }
    }
    function onScroll() { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* Add-to-cart feedback is handled entirely by the theme cart drawer, which
     opens automatically on a successful add. The old no-drawer toast + button
     flash were removed so there is a single, consistent, snappy experience. */

  function boot() {
    qsa('[data-afh]').forEach(function (hero) {
      if (hero.getAttribute('data-afh-mode') === 'scroll') {
        var track = hero.closest('[data-afh-scroll]');
        if (track) initScroll(track);
      } else {
        initHero(hero);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
